import { HelpCircle, MessageCircleQuestion } from 'lucide-react';
import { Card } from '@/components/ui/card';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: "How does Sentinel detect duplicate invoices?",
    answer: "Sentinel uses advanced AI algorithms to analyze invoice data including vendor names, amounts, dates, and invoice numbers. It employs both exact matching for identical duplicates and fuzzy matching algorithms to detect similar invoices with slight variations, achieving over 95% accuracy in duplicate detection."
  },
  {
    question: "What is financial reconciliation and why is it important?",
    answer: "Financial reconciliation is the process of matching bank transactions with accounting entries (like Tally ERP) to ensure accuracy. It's critical for detecting discrepancies, preventing fraud, maintaining accurate financial records, and ensuring compliance with accounting standards. Sentinel automates this time-consuming process, reducing reconciliation time from days to minutes."
  },
  {
    question: "How much does Sentinel cost?",
    answer: "Sentinel offers flexible pricing: a Free tier for small businesses processing up to 100 transactions per month, a Pro tier at ₹2,999/month for medium businesses with unlimited transactions and advanced features, and custom Enterprise pricing for large organizations requiring dedicated support and custom integrations."
  },
  {
    question: "Is my financial data secure with Sentinel?",
    answer: "Yes, security is our top priority. Sentinel employs bank-level encryption (AES-256), all data is stored in secure cloud infrastructure with row-level security policies, we conduct regular security audits, and we're compliant with industry standards. Your data is never shared with third parties and you maintain complete ownership."
  },
  {
    question: "Can Sentinel integrate with my existing accounting software?",
    answer: "Sentinel seamlessly integrates with popular accounting systems including Tally ERP, QuickBooks, and Zoho Books. It supports CSV imports for bank statements and XML imports for Tally entries. Our API allows custom integrations for enterprise clients with specialized systems."
  },
  {
    question: "How accurate is the AI-powered invoice analysis?",
    answer: "Our Gemini AI-powered invoice analysis achieves 98%+ accuracy in extracting key data points including vendor names, invoice numbers, amounts, and dates. The system learns from corrections and improves over time. For invoices flagged with uncertainty, the system prompts human review to ensure 100% accuracy."
  },
  {
    question: "What file formats does Sentinel support for imports?",
    answer: "Sentinel supports CSV files for bank statements with automatic column mapping, XML exports from Tally ERP for ledger entries, and PDF/image uploads for invoices that are processed via OCR. You can also use our REST API for programmatic data integration with your existing systems."
  },
  {
    question: "How long does the reconciliation process take?",
    answer: "Most reconciliation tasks complete in under 2 minutes, regardless of transaction volume. Our AI-powered matching engine processes thousands of transactions simultaneously, compared to manual reconciliation which can take days. You can review matches in real-time and approve them with one click."
  },
  {
    question: "Does Sentinel work offline?",
    answer: "Sentinel is a Progressive Web App (PWA) that caches essential data locally. You can view previously loaded data offline, and any actions taken offline will sync automatically when your connection is restored. For full functionality including AI analysis, an internet connection is required."
  },
  {
    question: "What kind of reports can I generate?",
    answer: "Sentinel provides comprehensive reporting including reconciliation summaries, duplicate payment analysis, vendor risk assessments, cash flow forecasts, and audit trails. All reports can be exported to PDF or CSV and scheduled for automatic delivery via email."
  }
];

export const FAQSection = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <section className="py-24 bg-gradient-to-b from-background to-surface/50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/15 border border-primary/20 mb-6">
            <HelpCircle className="w-4 h-4 text-primary-dark dark:text-primary" />
            <span className="text-sm font-medium text-primary-dark dark:text-primary">Got Questions?</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-primary bg-clip-text text-transparent pb-2">
            Frequently Asked Questions
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Everything you need to know about Sentinel and financial reconciliation
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {faqs.map((faq, index) => (
            <Card
              key={index}
              className="group relative p-6 border-border/50 bg-card/50 backdrop-blur-sm hover:bg-card hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5 transition-all duration-300"
            >
              {/* Number badge */}
              <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shadow-lg">
                {(index + 1).toString().padStart(2, '0')}
              </div>
              
              {/* Question icon */}
              <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <MessageCircleQuestion className="w-12 h-12 text-primary" />
              </div>
              
              {/* Content */}
              <div className="pt-2">
                <h3 className="text-lg font-semibold text-foreground mb-3 pr-10 group-hover:text-primary transition-colors">
                  {faq.question}
                </h3>
                <p className="text-muted-foreground leading-relaxed text-sm">
                  {faq.answer}
                </p>
              </div>
              
              {/* Bottom accent */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/0 via-primary/50 to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity rounded-b-lg" />
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
